import { ClockIcon, UserIcon } from "@heroicons/react/24/outline";
import {
    CommunityId,
    DEFAULT_TOUR_DURATION_MINUTES,
    formatDateBlock,
    formatTimeRange,
    splitTours,
    User,
    UserId,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TourDto } from "components/tours/types";
import {
    ContentFull,
    ContentHeader,
    ContentSection2Up,
    ContentSectionColumn,
} from "components/layout";
import { trpc } from "components/rpc/trpc";
import { TourEditor } from "components/tours/tour-editor";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { ClampedText } from "components/ui/clamped-text";
import { ClientLink } from "components/ui/entity-link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useMemo, useState } from "react";
import { breakNewlines } from "util/text";

interface CommunityToursTabProps {
    communityId: CommunityId;
    communityName: string;
    users: Record<UserId, User>;
}

const TourItem: React.FC<{
    tour: TourDto;
    users: Record<UserId, User>;
    onEdit: (tour: TourDto) => void;
    onDelete: (tour: TourDto) => void;
}> = ({ tour, users, onEdit, onDelete }) => {
    const userName = users[tour.userId]?.name || "";

    const handleEdit = useCallback(() => {
        onEdit(tour);
    }, [onEdit, tour]);

    const handleDelete = useCallback(() => {
        onDelete(tour);
    }, [onDelete, tour]);

    const dateBlock = formatDateBlock(tour.tourDate);
    const timeRange = tour.tourTime
        ? formatTimeRange(
              tour.tourTime,
              tour.tourDuration ?? DEFAULT_TOUR_DURATION_MINUTES,
          )
        : null;

    return (
        <li className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition p-4">
            <div className="flex items-start gap-4">
                {dateBlock ? (
                    <div className="flex flex-col items-center justify-center w-14 flex-shrink-0">
                        <div className="text-[11px] font-semibold uppercase text-brand-600">
                            {dateBlock.month}
                        </div>
                        <div className="text-2xl font-bold text-gray-900 leading-none">
                            {dateBlock.day}
                        </div>
                        <div className="text-[11px] text-gray-400">
                            {dateBlock.weekday}
                        </div>
                    </div>
                ) : null}
                <div className="flex-1 min-w-0">
                    {tour.placementId && tour.clientName ? (
                        <div className="font-medium text-gray-900">
                            <ClientLink
                                clientId={tour.placementId}
                                name={tour.clientName}
                            />
                        </div>
                    ) : null}
                    {timeRange ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <ClockIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{timeRange}</span>
                        </div>
                    ) : null}
                    {userName ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                            <UserIcon className="w-4 h-4" aria-hidden="true" />
                            <span>{userName}</span>
                        </div>
                    ) : null}
                    {tour.familyNotes ? (
                        <div className="mt-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Family Notes
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.familyNotes)}
                                </ClampedText>
                            </p>
                        </div>
                    ) : null}
                    {tour.privateNotes ? (
                        <div className="mt-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                                Internal Notes
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.privateNotes)}
                                </ClampedText>
                            </p>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center">
                    <EditButton backgroundColor="bg-white" onClick={handleEdit} />
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={handleDelete}
                        confirm={{
                            title: "Delete Tour",
                            message: "Are you sure you want to delete this tour?",
                        }}
                    />
                </div>
            </div>
        </li>
    );
};

export const CommunityToursTab: React.FC<CommunityToursTabProps> = props => {
    const { communityId, communityName, users } = props;
    const [editingTour, setEditingTour] = useState<TourDto | null | "new">(null);

    const trpcClient = trpc.useTRPC();
    const toursQuery = useQuery({
        ...trpcClient.tours.listByCommunity.queryOptions({ communityId }),
    });

    const tours = useMemo(() => toursQuery.data || [], [toursQuery.data]);

    const { upcoming, past } = useMemo(() => splitTours(tours), [tours]);

    const handleAddTour = useCallback(() => {
        setEditingTour("new");
    }, []);

    const handleEditTour = useCallback((tour: TourDto) => {
        setEditingTour(tour);
    }, []);

    const handleClose = useCallback(() => {
        setEditingTour(null);
    }, []);

    const handleSaved = useCallback(() => {
        toursQuery.refetch();
    }, [toursQuery]);

    const deleteMutation = useMutation({
        ...trpcClient.tours.delete.mutationOptions(),
        onSuccess: () => {
            toursQuery.refetch();
        },
    });

    const handleDeleteTour = useCallback(
        (tour: TourDto) => {
            deleteMutation.mutate({ id: tour.id });
        },
        [deleteMutation],
    );

    if (toursQuery.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                <PrimaryButton onClick={handleAddTour}>Add Tour</PrimaryButton>
            </div>

            <ContentSection2Up>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Upcoming Tours</ContentHeader>
                        {upcoming.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {upcoming.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        users={users}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">
                                No upcoming tours
                            </p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Past Tours</ContentHeader>
                        {past.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {past.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        users={users}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">No past tours</p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
            </ContentSection2Up>

            {editingTour !== null ? (
                <TourEditor
                    tour={editingTour === "new" ? null : editingTour}
                    communityId={communityId}
                    communityName={communityName}
                    clientName={
                        editingTour !== "new"
                            ? (editingTour?.clientName ?? undefined)
                            : undefined
                    }
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
};
